import type { DateValue } from "@internationalized/date";
import { Link, Navigate, useNavigate, useParams } from "@tanstack/react-router";
import type { Merchant } from "@unbox/api";
import { BarChart07, Calendar, CheckSquare, PresentationChart02 } from "@untitledui/icons";
import React, { useMemo, useState } from "react";
import { Bar, Legend, BarChart as RechartsBarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useLatestCurrencyConversion } from "@/api/currency";
import { usePortfolioMetricsOverview } from "@/api/metrics-overview";
import { DateRangePicker } from "@/components/application/date-picker/date-range-picker";
import { MetricChangeIndicator } from "@/components/application/metrics/metrics";
import { Table, TableCard } from "@/components/application/table/table";
import { Tooltip as BaseTooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { DateGranularitySelector } from "@/components/DateGranularitySelector";
import { useDisplayCurrencyStore } from "@/hooks/useDisplayCurrencyStore";
import { usePortfolio } from "@/providers/PortfolioProvider";
import { cx } from "@/utils/cx";
import { dateRangeToApiFormat, formatDate } from "@/utils/date-conversion";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { useDashboardDateFilter } from "../analytics-dashboard/hooks/use-dashboard-date-filter";

type TimeGranularity = "day" | "week" | "month";
type Currency = string;

// ============================================================================
// CURRENCY SELECTOR
// ============================================================================

interface CurrencySelectorProps {
  value: Currency;
  onChange: (currency: Currency) => void;
  size?: "sm" | "md";
  exchangeRate?: { gbp: number; eur: number };
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({ value, onChange, size = "sm", exchangeRate }) => {
  const buttonSize = size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm";
  const tooltipText = exchangeRate
    ? `1 USD = ${exchangeRate.gbp.toFixed(4)} GBP, ${exchangeRate.eur.toFixed(4)} EUR`
    : undefined;

  const currencies: Currency[] = ["USD", "GBP", "EUR"];

  const selector = (
    <div className="flex items-center gap-0.5 bg-gray-100 p-0.5 rounded-md">
      {currencies.map((currency) => (
        <button
          key={currency}
          type="button"
          className={cx(
            buttonSize,
            "font-medium rounded transition-colors",
            value === currency ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-primary",
          )}
          onClick={() => onChange(currency)}
        >
          {currency}
        </button>
      ))}
    </div>
  );

  if (!tooltipText) {
    return selector;
  }

  return (
    <BaseTooltip title={tooltipText} placement="bottom">
      <TooltipTrigger>{selector}</TooltipTrigger>
    </BaseTooltip>
  );
};

// Format large numbers compactly (e.g., 11500 -> "11.5k")
const formatCompactNumber = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(value);
};

// Format currency using Intl.NumberFormat with compact notation
const formatCurrencyValue = (value: number, currency: Currency): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(value);
};

// ============================================================================
// PORTFOLIO KPI CARD
// ============================================================================

interface ChartDataPoint {
  week: string;
  value: number;
}

interface PortfolioKPICardProps {
  title: string;
  value: string;
  change: string;
  changeTrend: "positive" | "negative";
  chartData: ChartDataPoint[];
  timePeriod: "daily" | "weekly" | "monthly";
  isCurrency?: boolean;
  displayCurrency?: Currency;
  currencySelector?: React.ReactNode;
}

// Format tooltip date label based on time period
const formatTooltipLabel = (dateString: string, timePeriod: "daily" | "weekly" | "monthly"): string => {
  const date = new Date(dateString);
  if (timePeriod === "monthly") {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
  if (timePeriod === "weekly") {
    return `Week of ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }
  // daily
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Format tooltip value (compact number or currency)
const formatTooltipValue = (value: number, isCurrency: boolean, currency?: Currency): string => {
  if (isCurrency && currency) {
    return formatCurrencyValue(value, currency);
  }
  return formatCompactNumber(value);
};

// Custom tooltip component with viewport boundary detection
const CustomChartTooltip = ({
  active,
  payload,
  label,
  coordinate,
  viewBox,
  formatter,
  labelFormatter,
  chartData,
  chartContainerRef,
}: any) => {
  if (!active || !payload || !payload.length) return null;

  const tooltipWidth = 200;
  const viewportWidth = window.innerWidth;

  // Get the chart's position on screen using the ref
  let absoluteX = coordinate?.x || 0;
  if (chartContainerRef?.current) {
    const chartRect = chartContainerRef.current.getBoundingClientRect();
    absoluteX = chartRect.left + (coordinate?.x || 0);
  }

  // Check if tooltip would overflow viewport on the right
  const shouldShowLeft = absoluteX + tooltipWidth + 10 > viewportWidth - 20;

  // Calculate spacing from chart data
  const chartWidth = viewBox?.width || 0;
  const dataLength = chartData?.length || 1;
  // Total available width per data point (includes bar + gaps)
  const widthPerPoint = chartWidth / dataLength;
  const offsetFromCenter = widthPerPoint / 2 + 10;

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        fontSize: "14px",
        padding: "12px",
        color: "#101828",
        transform: shouldShowLeft ? `translateX(calc(-100% - ${offsetFromCenter}px))` : undefined,
        pointerEvents: "none",
      }}
    >
      <p style={{ margin: 0, marginBottom: "4px", fontWeight: 600 }}>
        {labelFormatter ? labelFormatter(label) : label}
      </p>
      {payload.map((entry: any, index: number) => {
        const [formattedValue, name] = formatter ? formatter(entry.value) : [entry.value, entry.name];
        return (
          <div key={index} style={{ color: "#101828" }}>
            {name}: <strong>{formattedValue}</strong>
          </div>
        );
      })}
    </div>
  );
};

const PortfolioKPICard: React.FC<PortfolioKPICardProps> = ({
  title,
  value,
  change,
  changeTrend,
  chartData,
  timePeriod,
  isCurrency = false,
  displayCurrency,
  currencySelector,
}) => {
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="flex-1 bg-white rounded-lg px-6 py-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-tertiary text-base font-medium">{title}</span>
        <div className="inline-flex justify-start items-center gap-1">
          <MetricChangeIndicator type="trend" trend={changeTrend} value={change} />
          <span className="text-tertiary text-sm font-medium">vs prior period</span>
        </div>
      </div>

      <div className="items-start justify-between gap-1 flex">
        <div className="text-primary text-4xl font-medium leading-[40px]">{value}</div>
        {currencySelector}
      </div>

      <div className="h-[100px]" ref={chartContainerRef}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart data={chartData} margin={{ left: 4, right: 4, top: 4, bottom: 4 }}>
            <XAxis dataKey="week" hide={true} />
            <Tooltip
              wrapperStyle={{
                zIndex: 1000,
              }}
              content={
                <CustomChartTooltip
                  formatter={(val: number) => [formatTooltipValue(val, isCurrency, displayCurrency), title]}
                  labelFormatter={(label: string) => formatTooltipLabel(label, timePeriod)}
                  chartData={chartData}
                  chartContainerRef={chartContainerRef}
                />
              }
              allowEscapeViewBox={{ x: true, y: true }}
            />
            <Bar isAnimationActive={false} dataKey="value" fill="#5d3b81" radius={[2, 2, 0, 0]} maxBarSize={112} />
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ============================================================================
// PORTFOLIO PERFORMANCE METRICS
// ============================================================================

interface CurrencyConversion {
  usd: number;
  gbp: number;
  eur: number;
}

interface PortfolioPerformanceMetricsProps {
  merchants: Merchant[];
  data: ReturnType<typeof usePortfolioMetricsOverview>["data"];
  displayCurrency: Currency;
  currencyConversion: CurrencyConversion | undefined;
  timePeriod: "daily" | "weekly" | "monthly";
  currencySelector: React.ReactNode;
}

// Format percentage change (e.g., 0.4 -> "40%")
const formatPercentageChange = (change: number): string => {
  const percentFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    compactDisplay: "short",
    notation: "compact",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return percentFormatter.format(change / 100);
};

// Helper to format prior period dates for tooltip
const formatPriorPeriodDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Wrapper component for MetricChangeIndicator with tooltip
const MetricChangeWithTooltip: React.FC<{
  type: "simple" | "trend" | "modern";
  trend: "positive" | "negative";
  value: string;
  disabled?: boolean;
  priorPeriod?: { from: string; to: string };
}> = ({ type, trend, value, disabled, priorPeriod }) => {
  return (
    <BaseTooltip
      title={disabled ? "No trend data" : "Prior period"}
      description={
        disabled
          ? "No data available for the prior period"
          : priorPeriod
            ? `${formatPriorPeriodDate(priorPeriod.from)} - ${formatPriorPeriodDate(priorPeriod.to)}`
            : "vs prior period"
      }
      placement="top"
    >
      <TooltipTrigger className="inline-flex">
        <MetricChangeIndicator type={type} trend={trend} value={value} disabled={disabled} />
      </TooltipTrigger>
    </BaseTooltip>
  );
};

// Convert GMV value from merchant's currency to display currency
// All rates are stored as USD -> X (e.g. conversion.gbp = 1 USD in GBP)
const convertGmv = (
  value: number,
  merchantCurrency: Currency,
  displayCurrency: Currency,
  conversion: CurrencyConversion | undefined,
): number => {
  if (merchantCurrency === displayCurrency) return value;
  if (!conversion) return value;

  // Convert merchant currency to USD first, then to display currency
  const toUsd: Record<string, number> = {
    USD: 1,
    GBP: 1 / conversion.gbp,
    EUR: 1 / conversion.eur,
  };
  const fromUsd: Record<string, number> = {
    USD: 1,
    GBP: conversion.gbp,
    EUR: conversion.eur,
  };

  // If conversion rate is unknown for either currency (e.g. SEA currencies
  // like PHP, THB, VND), skip conversion so we don't mislabel the value.
  if (!(merchantCurrency in toUsd) || !(displayCurrency in fromUsd)) return value;

  return value * toUsd[merchantCurrency] * fromUsd[displayCurrency];
};

const PortfolioPerformanceMetrics: React.FC<PortfolioPerformanceMetricsProps> = ({
  merchants,
  data,
  displayCurrency,
  currencyConversion,
  timePeriod,
  currencySelector,
}) => {
  // Aggregate data across all merchants
  const aggregatedData = useMemo(() => {
    if (!data) return null;

    // Create a map of merchant ID to currency
    const merchantCurrencyMap = new Map<string, Currency>();
    for (const merchant of merchants) {
      merchantCurrencyMap.set(merchant.id, merchant.currency);
    }

    const merchantEntries = Object.entries(data.merchants);
    if (merchantEntries.length === 0) return null;

    // Aggregate KPIs with currency conversion
    let totalGmv = 0;
    let priorGmv = 0;
    for (const [merchantId, m] of merchantEntries) {
      const merchantCurrency = merchantCurrencyMap.get(merchantId) ?? "USD";
      totalGmv += convertGmv(m.kpis?.gmv?.current ?? 0, merchantCurrency, displayCurrency, currencyConversion);
      priorGmv += convertGmv(m.kpis?.gmv?.prior ?? 0, merchantCurrency, displayCurrency, currencyConversion);
    }
    const gmvChange = priorGmv > 0 ? (totalGmv - priorGmv) / priorGmv : 0;

    let totalVideos = 0;
    let priorVideos = 0;
    let totalCreators = 0;
    let priorCreators = 0;
    for (const [_, m] of merchantEntries) {
      totalVideos += m.kpis?.videos?.current ?? 0;
      priorVideos += m.kpis?.videos?.prior ?? 0;
      totalCreators += m.kpis?.creators?.current ?? 0;
      priorCreators += m.kpis?.creators?.prior ?? 0;
    }
    const videosChange = priorVideos > 0 ? (totalVideos - priorVideos) / priorVideos : 0;
    const creatorsChange = priorCreators > 0 ? (totalCreators - priorCreators) / priorCreators : 0;

    // Aggregate chart data by period with currency conversion for GMV
    const periodMap = new Map<string, { gmv: number; videos: number; creators: number }>();
    for (const [merchantId, merchant] of merchantEntries) {
      const merchantCurrency = merchantCurrencyMap.get(merchantId) ?? "USD";
      for (const period of merchant.chartData) {
        const existing = periodMap.get(period.periodStart) ?? { gmv: 0, videos: 0, creators: 0 };
        periodMap.set(period.periodStart, {
          gmv: existing.gmv + convertGmv(period.gmv, merchantCurrency, displayCurrency, currencyConversion),
          videos: existing.videos + period.videos,
          creators: existing.creators + period.creators,
        });
      }
    }

    const chartData = Array.from(periodMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([periodStart, values]) => ({
        week: periodStart,
        ...values,
      }));

    return {
      gmv: {
        value: formatCurrencyValue(totalGmv, displayCurrency),
        change: formatPercentageChange(gmvChange),
        trend: gmvChange >= 0 ? ("positive" as const) : ("negative" as const),
        chartData: chartData.map((d) => ({ week: d.week, value: d.gmv })),
      },
      videosPosted: {
        value: formatCompactNumber(totalVideos),
        change: formatPercentageChange(videosChange),
        trend: videosChange >= 0 ? ("positive" as const) : ("negative" as const),
        chartData: chartData.map((d) => ({ week: d.week, value: d.videos })),
      },
      creatorsPosting: {
        value: formatCompactNumber(totalCreators),
        change: formatPercentageChange(creatorsChange),
        trend: creatorsChange >= 0 ? ("positive" as const) : ("negative" as const),
        chartData: chartData.map((d) => ({ week: d.week, value: d.creators })),
      },
    };
  }, [data, merchants, displayCurrency, currencyConversion]);

  if (!aggregatedData) {
    return (
      <div className="w-full  rounded-lg p-6 flex items-center justify-center h-64">
        <span className="text-gray-500">Loading metrics...</span>
      </div>
    );
  }

  return (
    <div className="w-full  rounded-lg flex gap-8">
      {/* Right side - KPI cards */}
      <div className="flex-1 flex gap-4">
        <PortfolioKPICard
          title="GMV"
          value={aggregatedData.gmv.value}
          change={aggregatedData.gmv.change}
          changeTrend={aggregatedData.gmv.trend}
          chartData={aggregatedData.gmv.chartData}
          timePeriod={timePeriod}
          isCurrency={true}
          displayCurrency={displayCurrency}
          currencySelector={currencySelector}
        />
        <PortfolioKPICard
          title="Videos posted"
          value={aggregatedData.videosPosted.value}
          change={aggregatedData.videosPosted.change}
          changeTrend={aggregatedData.videosPosted.trend}
          chartData={aggregatedData.videosPosted.chartData}
          timePeriod={timePeriod}
        />
        <PortfolioKPICard
          title="Creators posting"
          value={aggregatedData.creatorsPosting.value}
          change={aggregatedData.creatorsPosting.change}
          changeTrend={aggregatedData.creatorsPosting.trend}
          chartData={aggregatedData.creatorsPosting.chartData}
          timePeriod={timePeriod}
        />
      </div>
    </div>
  );
};

// ============================================================================
// PERFORMANCE OVERVIEW TABLE
// ============================================================================

// Row links normally point at the bare `/merchant/$merchantId/...` route. Inside a
// workspace's overview (`workspaceId` set), they need the `/workspace/$workspaceId/...`
// prefix instead, or the access check on the plain merchant route rejects a user who
// only has this merchant through the workspace, not directly (UNB-2694).
const merchantLinkProps = (
  workspaceId: string | undefined,
  merchantId: string,
  path: "analytics-dashboard" | "product-explorer",
) =>
  workspaceId
    ? { to: `/workspace/$workspaceId/merchant/$merchantId/${path}` as string, params: { workspaceId, merchantId } }
    : { to: `/merchant/$merchantId/${path}` as string, params: { merchantId } };

interface PerformanceOverviewTableProps {
  merchants: Merchant[];
  data: ReturnType<typeof usePortfolioMetricsOverview>["data"];
  workspaceId?: string;
}

const PerformanceOverviewTable: React.FC<PerformanceOverviewTableProps> = ({ merchants, data, workspaceId }) => {
  const priorPeriod = data?.priorPeriod;

  const tableData = useMemo(() => {
    if (!data) return [];

    return merchants
      .map((merchant) => {
        const merchantData = data.merchants[merchant.id];
        if (!merchantData) return null;

        const kpis = merchantData.kpis;

        return {
          id: merchant.id,
          name: merchant.name,
          logoUrl: merchant.logoUrl,
          currency: merchant.currency,
          gmv: kpis.gmv?.current ?? 0,
          gmvChange: kpis.gmv?.change ?? 0,
          gmvPrior: kpis.gmv?.prior ?? 0,
          orders: kpis.orders?.current ?? 0,
          ordersChange: kpis.orders?.change ?? 0,
          ordersPrior: kpis.orders?.prior ?? 0,
          videosPosted: kpis.videos?.current ?? 0,
          videosChange: kpis.videos?.change ?? 0,
          videosPrior: kpis.videos?.prior ?? 0,
          creatorsPosting: kpis.creators?.current ?? 0,
          creatorsChange: kpis.creators?.change ?? 0,
          creatorsPrior: kpis.creators?.prior ?? 0,
        };
      })
      .filter((row): row is NonNullable<typeof row> => row !== null);
  }, [merchants, data]);

  if (tableData.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-primary rounded-lg px-8 py-6">
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <PresentationChart02 className="w-5 h-5 text-tertiary" />
          <h4 className="text-base font-medium text-secondary">Performance Overview</h4>
        </div>

        <TableCard.Root className="bg-secondary_alt">
          <Table aria-label="Performance Overview" selectionMode="none">
            <Table.Header>
              <Table.Head isRowHeader className="font-medium text-xs min-w-[200px]">
                Brand
              </Table.Head>
              <Table.Head className="text-xs font-medium">GMV</Table.Head>
              <Table.Head className="text-xs font-medium">Orders</Table.Head>
              <Table.Head className="text-xs font-medium">Videos Posted</Table.Head>
              <Table.Head className="text-xs font-medium">Creators Posting</Table.Head>
            </Table.Header>
            <Table.Body items={tableData}>
              {(row) => (
                <Table.Row id={row.id} className="bg-primary">
                  <Table.Cell className="text-sm font-medium">
                    <Link {...merchantLinkProps(workspaceId, row.id, "analytics-dashboard")} target="_blank">
                      <div className="flex items-center gap-3 text-brand-secondary hover:underline">
                        {row.logoUrl ? (
                          <img src={row.logoUrl} alt={row.name} className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                            {row.name.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        {row.name}
                      </div>
                    </Link>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    <div className="flex items-center gap-2">
                      <span>{formatCurrencyValue(row.gmv, row.currency)}</span>
                      <MetricChangeWithTooltip
                        type="simple"
                        trend={row.gmvChange >= 0 ? "positive" : "negative"}
                        value={formatPercentageChange(row.gmvChange)}
                        disabled={row.gmvPrior === 0}
                        priorPeriod={priorPeriod}
                      />
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    <div className="flex items-center gap-2">
                      <span>{row.orders.toLocaleString()}</span>
                      <MetricChangeWithTooltip
                        type="simple"
                        trend={row.ordersChange >= 0 ? "positive" : "negative"}
                        value={formatPercentageChange(row.ordersChange)}
                        disabled={row.ordersPrior === 0}
                        priorPeriod={priorPeriod}
                      />
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    <div className="flex items-center gap-2">
                      <span>{row.videosPosted.toLocaleString()}</span>
                      <MetricChangeWithTooltip
                        type="simple"
                        trend={row.videosChange >= 0 ? "positive" : "negative"}
                        value={formatPercentageChange(row.videosChange)}
                        disabled={row.videosPrior === 0}
                        priorPeriod={priorPeriod}
                      />
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    <div className="flex items-center gap-2">
                      <span>{row.creatorsPosting.toLocaleString()}</span>
                      <MetricChangeWithTooltip
                        type="simple"
                        trend={row.creatorsChange >= 0 ? "positive" : "negative"}
                        value={formatPercentageChange(row.creatorsChange)}
                        disabled={row.creatorsPrior === 0}
                        priorPeriod={priorPeriod}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </TableCard.Root>
      </div>
    </div>
  );
};

// ============================================================================
// TOP SELLING PRODUCTS TABLE
// ============================================================================

interface TopSellingProductsTableProps {
  merchants: Merchant[];
  data: ReturnType<typeof usePortfolioMetricsOverview>["data"];
  workspaceId?: string;
}

const TopSellingProductsTable: React.FC<TopSellingProductsTableProps> = ({ merchants, data, workspaceId }) => {
  const tableData = useMemo(() => {
    if (!data) return [];

    return merchants
      .map((merchant) => {
        const topProduct = data.merchants[merchant.id]?.topProduct;
        if (!topProduct) return null;

        return {
          id: `${merchant.id}-${topProduct.productId}`,
          merchantId: merchant.id,
          merchantName: merchant.name,
          merchantLogo: merchant.logoUrl,
          merchantCurrency: merchant.currency,
          productId: topProduct.productId,
          productName: topProduct.productName,
          productThumbnail: topProduct.thumbnail,
          gmv: topProduct.revenue,
          unitsSold: topProduct.unitSales,
          // TODO: Need change data from API
          gmvChange: 0,
          unitsSoldChange: 0,
        };
      })
      .filter((row): row is NonNullable<typeof row> => row !== null);
  }, [merchants, data]);

  if (tableData.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-primary rounded-lg px-8 py-6">
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-tertiary" />
          <h4 className="text-base font-medium text-secondary">Top Selling Products</h4>
        </div>

        <TableCard.Root className="bg-secondary_alt">
          <Table aria-label="Top Selling Products" selectionMode="none">
            <Table.Header>
              <Table.Head isRowHeader className="font-medium text-xs min-w-[200px]">
                Product
              </Table.Head>
              <Table.Head className="text-xs font-medium">GMV</Table.Head>
              <Table.Head className="text-xs font-medium">Units Sold</Table.Head>
              <Table.Head className="text-xs font-medium">Brand</Table.Head>
            </Table.Header>
            <Table.Body items={tableData}>
              {(row) => (
                <Table.Row id={row.id} className="bg-primary">
                  <Table.Cell className="text-sm font-medium">
                    <Link
                      {...merchantLinkProps(workspaceId, row.merchantId, "product-explorer")}
                      search={{ product_id: row.productId }}
                      target="_blank"
                    >
                      <div className="flex items-center gap-3">
                        {row.productThumbnail ? (
                          <img
                            src={row.productThumbnail}
                            alt={row.productName}
                            className="w-10 h-10 rounded object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                            N/A
                          </div>
                        )}
                        <span className="max-w-md truncate text-brand-secondary hover:underline">
                          {row.productName}
                        </span>
                      </div>
                    </Link>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    <div className="flex items-center gap-2">
                      <span>{formatCurrencyValue(row.gmv, row.merchantCurrency)}</span>
                      {row.gmvChange !== 0 && (
                        <MetricChangeIndicator
                          type="trend"
                          trend={row.gmvChange >= 0 ? "positive" : "negative"}
                          value={formatPercentageChange(row.gmvChange)}
                        />
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    <div className="flex items-center gap-2">
                      <span>{row.unitsSold.toLocaleString()}</span>
                      {row.unitsSoldChange !== 0 && (
                        <MetricChangeIndicator
                          type="trend"
                          trend={row.unitsSoldChange >= 0 ? "positive" : "negative"}
                          value={formatPercentageChange(row.unitsSoldChange)}
                        />
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-sm font-medium">
                    <Link {...merchantLinkProps(workspaceId, row.merchantId, "product-explorer")} target="_blank">
                      <div className="flex items-center gap-3 text-brand-secondary hover:underline">
                        {row.merchantLogo ? (
                          <img src={row.merchantLogo} alt={row.merchantName} className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                            {row.merchantName.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        {row.merchantName}
                      </div>
                    </Link>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </TableCard.Root>
      </div>
    </div>
  );
};

// ============================================================================
// GMV OVER TIME CHART
// ============================================================================

interface GmvOverTimeChartProps {
  merchants: Merchant[];
  data: ReturnType<typeof usePortfolioMetricsOverview>["data"];
  displayCurrency: Currency;
  currencyConversion: CurrencyConversion | undefined;
  dateRange: { start: DateValue; end: DateValue } | null;
  currencySelector: React.ReactNode;
}

// Generate a color for each merchant
const MERCHANT_COLORS = [
  "#7c3aed", // violet-600
  "#a78bfa", // violet-400
  "#c4b5fd", // violet-300
  "#ddd6fe", // violet-200
  "#8b5cf6", // violet-500
  "#6d28d9", // violet-700
];

const GmvOverTimeChart: React.FC<GmvOverTimeChartProps> = ({
  merchants,
  data,
  displayCurrency,
  currencyConversion,
  dateRange,
  currencySelector,
}) => {
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  const chartData = useMemo(() => {
    if (!data) return [];

    // Collect all periods across all merchants
    const periodMap = new Map<string, Record<string, number>>();

    for (const merchant of merchants) {
      const merchantData = data.merchants[merchant.id];
      if (!merchantData) continue;

      for (const period of merchantData.chartData) {
        const existing = periodMap.get(period.periodStart) ?? {};
        const gmv = convertGmv(period.gmv ?? 0, merchant.currency, displayCurrency, currencyConversion);
        existing[merchant.id] = gmv;
        periodMap.set(period.periodStart, existing);
      }
    }

    // Convert to array sorted by period
    return Array.from(periodMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([periodStart, values]) => {
        // Format the period label (e.g., "Jan", "Feb")
        const date = new Date(periodStart);
        const label = date.toLocaleDateString("en-US", { month: "short" });

        return {
          period: label,
          ...values,
        };
      });
  }, [merchants, data, displayCurrency, currencyConversion]);

  // Format date range for display
  const dateRangeLabel = useMemo(() => {
    if (!dateRange) return null;

    const startDate = new Date(dateRange.start.year, dateRange.start.month - 1, dateRange.start.day);
    const endDate = new Date(dateRange.end.year, dateRange.end.month - 1, dateRange.end.day);

    return `All Time: ${formatDate(startDate)} - ${formatDate(endDate)}`;
  }, [dateRange]);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-primary rounded-lg px-8 py-6">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-tertiary" />
            <h4 className="text-base font-medium text-secondary">GMV over time</h4>
            {currencySelector}
          </div>
          <span className="text-sm text-tertiary">{dateRangeLabel}</span>
        </div>

        {/* Chart */}
        <div className="h-80" ref={chartContainerRef}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart data={chartData} margin={{ left: 0, right: 0, top: 20, bottom: 20 }}>
              <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#667085" }} />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#667085" }}
                tickFormatter={(value) => formatCurrencyValue(value, displayCurrency)}
              />
              <Tooltip
                wrapperStyle={{
                  zIndex: 1000,
                }}
                content={({ active, payload, label, coordinate, viewBox }: any) => {
                  if (!active || !payload || !payload.length) return null;

                  const tooltipWidth = 250;
                  const viewportWidth = window.innerWidth;

                  // Get the chart's position on screen using the ref
                  let absoluteX = coordinate?.x || 0;
                  if (chartContainerRef.current) {
                    const chartRect = chartContainerRef.current.getBoundingClientRect();
                    absoluteX = chartRect.left + (coordinate?.x || 0);
                  }

                  const wouldOverflowRight = absoluteX + tooltipWidth + 10 > viewportWidth - 20;

                  return (
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        padding: "12px",
                        color: "#101828",
                        transform: wouldOverflowRight ? `translateX(calc(-100% - 20px))` : undefined,
                        pointerEvents: "none",
                      }}
                    >
                      <p style={{ margin: 0, marginBottom: "8px", fontWeight: 600, color: "#101828" }}>{label}</p>
                      {payload.map((entry: any, index: number) => {
                        const merchant = merchants.find((m) => m.id === entry.dataKey);
                        const merchantName = merchant?.name ?? entry.dataKey;
                        return (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "4px",
                              color: "#101828",
                            }}
                          >
                            <div
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: entry.fill,
                              }}
                            />
                            <span>
                              {merchantName}: <strong>{formatCurrencyValue(entry.value, displayCurrency)}</strong>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
                allowEscapeViewBox={{ x: true, y: true }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value: string) => {
                  const merchant = merchants.find((m) => m.id === value);
                  return <span className="text-black">{merchant?.name ?? value}</span>;
                }}
              />
              {merchants.map((merchant, index) => (
                <Bar
                  key={merchant.id}
                  dataKey={merchant.id}
                  stackId="gmv"
                  fill={MERCHANT_COLORS[index % MERCHANT_COLORS.length]}
                  radius={index === merchants.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div
      className={cx(
        "w-full h-full",
        "inline-flex flex-col justify-start items-center gap-12",
        "bg-gradient-to-b from-[#FFE1BD]/40 via-[#FFF9C5]/40 to-[#FFFFFF]/15",
      )}
    >
      <div className={cx(LAYOUT_CONSTRAINTS.CONTAINER_WIDTH, "flex flex-col gap-8 px-16 pt-16 pb-8")}>
        {/* Page Header */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center gap-4 pb-4">
            <div className="flex items-center gap-3">
              <BarChart07 className="w-5 h-5 text-tertiary" />
              <div className="text-secondary text-md font-medium">Performance Overview</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-48 animate-pulse rounded-lg bg-gray-200" />
              <div className="h-10 w-32 animate-pulse rounded-lg bg-gray-200" />
            </div>
          </div>
          <div className="flex justify-between items-end pb-20">
            <div className="h-9 w-96 animate-pulse rounded bg-gray-200" />
            <div className="flex -space-x-1">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-8 h-8 rounded border-2 border-white animate-pulse bg-gray-200" />
              ))}
            </div>
          </div>
        </div>

        {/* KPI Cards - 3 cards */}
        <div className="w-full rounded-lg flex gap-8">
          <div className="flex-1 flex gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-1 bg-white rounded-lg px-6 py-5 flex flex-col gap-4">
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                <div className="h-10 w-32 animate-pulse rounded bg-gray-200" />
                <div className="h-[100px] animate-pulse rounded bg-gray-100" />
              </div>
            ))}
          </div>
        </div>

        {/* Performance Overview Table */}
        <div className="w-full bg-primary rounded-lg px-8 py-6">
          <div className="flex flex-col gap-6">
            <div className="h-5 w-48 animate-pulse rounded bg-gray-200" />
            <div className="h-64 animate-pulse rounded-lg bg-gray-100" />
          </div>
        </div>

        {/* Top Selling Products Table */}
        <div className="w-full bg-primary rounded-lg px-8 py-6">
          <div className="flex flex-col gap-6">
            <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
            <div className="h-48 animate-pulse rounded-lg bg-gray-100" />
          </div>
        </div>

        {/* GMV Over Time Chart */}
        <div className="w-full bg-primary rounded-lg px-8 py-6">
          <div className="flex flex-col gap-6">
            <div className="h-5 w-32 animate-pulse rounded bg-gray-200" />
            <div className="h-80 animate-pulse rounded-lg bg-gray-100" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Renders the performance overview for a given set of merchants. Shared between the
// global portfolio ("Your Portfolio") and a Workspace Admin's workspace overview (UNB-1953) —
// both surface identical content, just scoped to a different merchant list.
export const PortfolioAnalyticsContent: React.FC<{ merchants: Merchant[]; workspaceId?: string }> = ({
  merchants,
  workspaceId,
}) => {
  const navigate = useNavigate();
  const { data: currency, isLoading: isLoadingCurrency } = useLatestCurrencyConversion();
  const { displayCurrency, setDisplayCurrency } = useDisplayCurrencyStore();

  const [timeGranularity, setTimeGranularity] = useState<TimeGranularity>("week");
  const timePeriod = useMemo(() => {
    switch (timeGranularity) {
      case "day":
        return "daily";
      case "week":
        return "weekly";
      case "month":
        return "monthly";
    }
  }, [timeGranularity]);
  const { dateRange, isApplied, changeDate, limits } = useDashboardDateFilter();
  const [tempDateRange, setTempDateRange] = useState<{ start: DateValue; end: DateValue } | null>(null);
  const { data, isLoading } = usePortfolioMetricsOverview(timePeriod, dateRangeToApiFormat(dateRange), workspaceId);

  // Custom clear function that resets to default (unset state)
  const handleClearDateRange = () => {
    // Clear from URL (keep only tab param)
    navigate({
      replace: true,
    });

    // Clear the date range (set to null in Zustand store)
    changeDate(null);
  };

  if (isLoading || isLoadingCurrency) {
    return <LoadingSkeleton />;
  }

  return (
    <div
      className={cx(
        "w-full h-full overflow-y-scroll",
        "inline-flex flex-col justify-start items-center gap-12", // Internal layout
        "bg-gradient-to-b from-[#FFE1BD]/40 via-[#FFF9C5]/40 to-[#FFFFFF]/15",
      )}
    >
      <div className={cx(LAYOUT_CONSTRAINTS.CONTAINER_WIDTH, "flex flex-col gap-8 px-16 pt-16 pb-8")}>
        {/* Page Header */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center gap-4 pb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <BarChart07 className="w-5 h-5 text-tertiary" />
              <div className="text-secondary text-md font-medium">Performance Overview</div>
            </div>

            <div className="flex items-start gap-3">
              <DateRangePicker
                isActive={isApplied}
                textClassName="text-sm font-semibold"
                buttonClassName="h-10"
                aria-label="Date range picker"
                shouldCloseOnSelect={false}
                selected={dateRange}
                value={tempDateRange || dateRange}
                onChange={setTempDateRange}
                onReset={handleClearDateRange}
                onApply={() => changeDate(tempDateRange)}
                maxValue={limits?.maxDate}
                minValue={limits?.minDate}
                limits={limits}
                showPresets={true}
                resetText="Reset to last 31 days"
              />
              <DateGranularitySelector value={timeGranularity} onChange={setTimeGranularity} />
            </div>
          </div>
          <div className="flex justify-between items-end pb-20">
            <h1 className="text-indigo-950 text-3xl max-w-120 font-normal leading-9">
              Core performance metrics across your portfolio
            </h1>
            <div className="flex -space-x-1">
              {merchants.map((merchant) =>
                merchant.logoUrl ? (
                  <img
                    key={merchant.id}
                    src={merchant.logoUrl}
                    alt={merchant.name}
                    className="w-8 h-8 rounded border-2 border-white object-cover"
                  />
                ) : (
                  <div
                    key={merchant.id}
                    className="w-8 h-8 rounded border-2 border-white bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500"
                  >
                    {merchant.name.slice(0, 2).toUpperCase()}
                  </div>
                ),
              )}
            </div>
          </div>
        </div>

        {/* Portfolio Performance Metrics */}
        <PortfolioPerformanceMetrics
          merchants={merchants}
          data={data}
          displayCurrency={displayCurrency}
          currencyConversion={currency}
          timePeriod={timePeriod}
          currencySelector={
            <CurrencySelector
              value={displayCurrency}
              onChange={setDisplayCurrency}
              exchangeRate={currency ? { gbp: currency.gbp, eur: currency.eur } : undefined}
            />
          }
        />

        {/* Performance Overview Table */}
        <PerformanceOverviewTable merchants={merchants} data={data} workspaceId={workspaceId} />

        {/* Top Selling Products Table */}
        <TopSellingProductsTable merchants={merchants} data={data} workspaceId={workspaceId} />

        {/* GMV Over Time Chart */}
        <GmvOverTimeChart
          merchants={merchants}
          data={data}
          displayCurrency={displayCurrency}
          currencyConversion={currency}
          dateRange={dateRange}
          currencySelector={
            <CurrencySelector
              value={displayCurrency}
              onChange={setDisplayCurrency}
              exchangeRate={currency ? { gbp: currency.gbp, eur: currency.eur } : undefined}
            />
          }
        />
      </div>
    </div>
  );
};

export const PortfolioAnalyticsDashboard: React.FC = () => {
  const { enabled, merchants } = usePortfolio();
  // Compared against `false` rather than falsy on purpose: an `undefined` value means the
  // portfolio state isn't resolved yet, and we don't want to redirect away on that.
  if (enabled === false) return <Navigate to="/" />;
  return <PortfolioAnalyticsContent merchants={merchants} />;
};
